import { useState } from 'react';
import type { Phase, Plan, Step } from '../lib/types';

interface Props {
  plan: Plan;
}

export default function PlanView({ plan }: Props) {
  if (!plan || !Array.isArray(plan.phases)) {
    return (
      <div className="rounded border border-slate-200 bg-white p-4 text-sm text-slate-500">
        (no plan yet)
      </div>
    );
  }

  // Auto-collapse phases for big plans to keep the page scrollable.
  const startCollapsed = plan.phases.length >= 4;

  return (
    <div className="rounded border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide">
          Plan
        </h3>
        <span className="text-xs text-slate-500">
          {plan.total_phases} phase{plan.total_phases === 1 ? '' : 's'} ·{' '}
          {plan.total_steps} step{plan.total_steps === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-sm text-slate-700 mb-3">{plan.summary}</p>
      <div className="space-y-2">
        {plan.phases.map((phase, i) => (
          <PhaseBlock
            key={phase.id}
            phase={phase}
            index={i + 1}
            startOpen={!startCollapsed}
          />
        ))}
      </div>
    </div>
  );
}

function PhaseBlock({
  phase,
  index,
  startOpen,
}: {
  phase: Phase;
  index: number;
  startOpen: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const riskyStepCount = phase.steps.filter((s) => s.risks && s.risks.length > 0).length;

  return (
    <div className="border border-slate-200 rounded">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-slate-400 font-mono w-8">
            {open ? '▾' : '▸'} {index}.
          </span>
          <span className="font-medium text-sm text-slate-800 truncate">
            {phase.name}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {riskyStepCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
              ⚠ {riskyStepCount} risk{riskyStepCount > 1 ? 's' : ''}
            </span>
          )}
          <span>{phase.steps.length} steps</span>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100">
          <p className="text-xs text-slate-600 mb-2">{phase.description}</p>
          {phase.acceptance_criteria && phase.acceptance_criteria.length > 0 && (
            <div className="mb-2 rounded bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
              <span className="font-medium">Phase acceptance: </span>
              {phase.acceptance_criteria.join(' · ')}
            </div>
          )}
          <ul className="space-y-1.5">
            {phase.steps.map((step) => (
              <StepRow key={step.id} step={step} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StepRow({ step }: { step: Step }) {
  const [expanded, setExpanded] = useState(false);
  const hasRisks = step.risks && step.risks.length > 0;
  const hasFiles = (step.files_likely_to_change?.length ?? 0) > 0;

  return (
    <li className="border-l-2 border-slate-200 pl-2 py-0.5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-left w-full hover:bg-slate-50 px-1 py-0.5 rounded"
      >
        <div className="flex items-start gap-2">
          <span className="font-mono text-[10px] text-slate-500 pt-1 w-12 shrink-0">
            {step.id}
          </span>
          <span className="text-sm flex-1 min-w-0">{step.description}</span>
          {hasRisks && (
            <span className="text-amber-600 text-xs" title={step.risks!.join('\n')}>
              ⚠
            </span>
          )}
        </div>
      </button>
      {expanded && (
        <div className="ml-14 mt-1 mb-1 space-y-1 text-xs">
          {(step.acceptance_criteria?.length ?? 0) > 0 && (
            <div>
              <span className="text-slate-500">Acceptance: </span>
              <ul className="list-disc list-inside text-slate-700 ml-1">
                {step.acceptance_criteria.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}
          {hasFiles && (
            <div>
              <span className="text-slate-500">Files: </span>
              <span className="font-mono text-slate-700">
                {step.files_likely_to_change.join(', ')}
              </span>
            </div>
          )}
          {hasRisks && (
            <div>
              <span className="text-amber-700">Risks: </span>
              <ul className="list-disc list-inside text-amber-900 ml-1">
                {step.risks!.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
